import { Injectable } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    CanActivate,
    CanActivateChild,
    CanMatch,
    Data,
    Route,
    Router,
    RouterStateSnapshot,
    UrlSegment,
    UrlTree
} from '@angular/router';
import { AuthenticationService } from '@core/services';
import { IUser, UserType } from '@core/interfaces';
import { BusinessRoutePaths } from '@core/configs/route-paths';


@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate, CanActivateChild, CanMatch {
    constructor(
        private router: Router,
        private authenticationService: AuthenticationService
    ) { }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
        return this.authorize(this.getRouteData(route), state.url);
    }

    canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
        return this.authorize(this.getRouteData(route), state.url);
    }

    canMatch(route: Route, segments: UrlSegment[]): boolean | UrlTree {
        const returnUrl = `/${segments.map(segment => segment.path).join('/')}`;

        return this.authorize(route.data ?? {}, returnUrl);
    }

    private authorize(data: Data = {}, returnUrl: string): boolean | UrlTree {
        const currentUser = this.authenticationService.currentUserValue;
        if (!currentUser) {
            return this.router.createUrlTree(['/sign-in'], { queryParams: { returnUrl } });
        }

        const activeType = this.getActiveUserType(currentUser);
        const allowedRoles = this.getAllowedRoles(data);
        if (allowedRoles.length && !allowedRoles.includes(activeType)) {
            return this.router.createUrlTree([activeType === UserType.Business ? BusinessRoutePaths.root : '/']);
        }

        return true;
    }

    private getActiveUserType(currentUser: IUser): UserType {
        return this.authenticationService.currentUserActiveType || currentUser.userType;
    }

    private getAllowedRoles(data: Data): UserType[] {
        if (Array.isArray(data.roles)) {
            return data.roles;
        }

        return data.role ? [data.role] : [];
    }

    private getRouteData(route: ActivatedRouteSnapshot): Data {
        const routeChain: ActivatedRouteSnapshot[] = [];
        let currentRoute: ActivatedRouteSnapshot | null = route;

        while (currentRoute) {
            routeChain.unshift(currentRoute);
            currentRoute = currentRoute.parent;
        }

        return routeChain.reduce<Data>((data, routeSnapshot) => ({ ...data, ...routeSnapshot.data }), {});
    }
}
